import {Form, FormField, FormSubmitHandler, Separator, showNotification} from 'platform/components';
import {Heading, VStack} from 'platform/foundation';

import {
  useGetSalesWarrantySettingsQuery,
  useSetSalesWarrantySettingsMutation,
} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {SettingsFooter, SettingsSection, SettingsTemplate} from '@dms/settings/shared';
import {handleApiError} from '@dms/shared';

import {ApiError} from 'shared/web';

import {WarrantyProgramList} from './components/WarrantyProgramList';
import {
  warrantySettingsFormSchema,
  WarrantySettingsFormValues,
} from './components/warrantySettingsFormSchema';

export function Warranty() {
  const {data: settings, isLoading, isError} = useGetSalesWarrantySettingsQuery();

  const [setSalesWarrantySettings] = useSetSalesWarrantySettingsMutation();

  const defaultValues: WarrantySettingsFormValues = {
    defaultDuration: settings?.defaultDuration ?? null,
    warrantyProgramDurationOverrideDisabled:
      settings?.warrantyProgramDurationOverrideDisabled ?? false,
  };

  const handleSubmit: FormSubmitHandler<WarrantySettingsFormValues> = async (
    values,
    _setErrors,
    reset,
    blockerContext
  ) => {
    await setSalesWarrantySettings({salesWarrantySettingsRequestBody: values})
      .unwrap()
      .then(() => {
        showNotification.success(i18n.t('general.notifications.changesSaved'));
        reset(undefined, {keepValues: true});
      })
      .catch((error: ApiError) =>
        handleApiError(error, {signalApiError: blockerContext?.reportApiFailure})
      );
  };

  return (
    <SettingsTemplate
      isLoading={isLoading}
      isError={isError}
      data-testid={testIds.settings.warranty('template')}
      header={{
        title: i18n.t('page.salesSettings.labels.warranty'),
        subtitle: i18n.t('entity.warranty.labels.warrantyDescription'),
      }}
    >
      <Form<WarrantySettingsFormValues>
        experimentalZodSchema={warrantySettingsFormSchema}
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
        shouldWatchForUnsavedChanges
      >
        {(control, formApi) => (
          <>
            <SettingsSection>
              <VStack spacing={4} align="flex-start">
                <FormField
                  type="number"
                  control={control}
                  name="defaultDuration"
                  isStepperVisible
                  label={i18n.t('entity.warranty.labels.defaultDuration')}
                  data-testid={testIds.settings.warranty('defaultDuration')}
                />

                <FormField
                  type="checkbox"
                  control={control}
                  name="warrantyProgramDurationOverrideDisabled"
                  label={i18n.t('entity.warranty.labels.durationOverrideDisabled')}
                  helperText={i18n.t('entity.warranty.labels.durationOverrideDisabledHelper')}
                  data-testid={testIds.settings.warranty('durationOverrideDisabled')}
                />
              </VStack>
            </SettingsSection>

            <Separator spacing={4} />

            <SettingsSection>
              <VStack spacing={4}>
                <Heading size={4}>{i18n.t('entity.warranty.labels.warrantyPrograms')}</Heading>
                <WarrantyProgramList data-testid={testIds.settings.warranty('list')} />
              </VStack>
            </SettingsSection>

            <SettingsFooter
              data-testid={testIds.settings.warranty('footer')}
              actions={[
                {
                  type: 'button',
                  title: i18n.t('general.actions.discardChanges'),
                  variant: 'secondary',
                  onClick: () => formApi.reset(defaultValues),
                },
                {
                  type: 'form-button',
                  control,
                  buttonType: 'submit',
                  title: i18n.t('general.actions.saveChanges'),
                },
              ]}
            />
          </>
        )}
      </Form>
    </SettingsTemplate>
  );
}
